import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { FormControl } from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { User } from "@/interfaces";
import { cn, errorHandler } from "@/lib/utils";
import { userService } from "@/services/api/user";
import { Check, ChevronsUpDown } from "lucide-react";
import { useEffect, useState } from "react";
import { Skeleton } from "./skeleton";

interface UserSelectProps {
  value?: number | number[];
  onChange: (value: number | number[]) => void;
  onUsersLoaded?: () => void;
  multiple?: boolean;
}

const UserSelect = ({
  value,
  onChange,
  onUsersLoaded,
  multiple = false,
}: UserSelectProps) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const loadDrivers = async () => {
      try {
        setLoading(true);
        const response = await userService.getUsers();

        setUsers(response.data);
        if (onUsersLoaded) {
          onUsersLoaded();
        }
      } catch (err) {
        errorHandler(err, "Erro ao carregar usuários.");
      } finally {
        setLoading(false);
      }
    };

    loadDrivers();
  }, [onUsersLoaded]);

  const handleSelect = (driverId: number) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(driverId)
        ? currentValues.filter((id) => id !== driverId)
        : [...currentValues, driverId];
      onChange(newValues);
    } else {
      onChange(driverId);
      setOpen(false);
    }
  };

  const handleSelectAll = () => {
    if (multiple) {
      const allUsersIds = users.map((user) => user.id);
      onChange(allUsersIds);
    }
  };

  const handleDeselectAll = () => {
    if (multiple) {
      onChange([]);
    }
  };

  const getSelectedLabel = () => {
    if (multiple) {
      const selectedUsers = users.filter((user) =>
        Array.isArray(value) ? value.includes(user.id) : false,
      );
      return selectedUsers.length > 0
        ? selectedUsers.length === 1
          ? selectedUsers[0].name
          : `${selectedUsers.length} Usuários selecionados`
        : "Selecione os usuários";
    } else {
      return value
        ? users.find((user) => user.id === value)?.name
        : "Selecione um usuário";
    }
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full bg-muted" />{" "}
        <Popover>
          <PopoverContent className="w-full p-2">
            <div className="space-y-2">
              <Skeleton className="h-8 w-full bg-muted" />
              <Skeleton className="h-8 w-full bg-muted" />
              <Skeleton className="h-8 w-full bg-muted" />
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <FormControl>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={loading}
            className={cn(
              "w-full justify-between dark:border-white overflow-hidden",
              (!value ||
                (multiple && Array.isArray(value) && value.length === 0)) &&
                "text-muted-foreground",
            )}
          >
            {getSelectedLabel()}
            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
          </Button>
        </FormControl>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command
          filter={(value, search) => {
            if (value.toLowerCase().includes(search.toLowerCase())) return 1;
            return 0;
          }}
        >
          <CommandInput placeholder="Procure um usuário..." className="h-9" />
          <CommandList>
            <CommandEmpty>Nenhum usuário encontrado.</CommandEmpty>
            {multiple && (
              <CommandGroup>
                <div className="flex gap-2 p-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 justify-start"
                    onClick={handleSelectAll}
                  >
                    Marcar todos
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 justify-start"
                    onClick={handleDeselectAll}
                  >
                    Desmarcar todos
                  </Button>
                </div>
              </CommandGroup>
            )}
            <CommandGroup>
              {users.map((user) => (
                <CommandItem
                  value={user.name}
                  key={user.id}
                  onSelect={() => handleSelect(user.id)}
                >
                  {user.name}
                  <Check
                    className={cn(
                      "ml-auto",
                      multiple
                        ? Array.isArray(value) && value.includes(user.id)
                          ? "opacity-100"
                          : "opacity-0"
                        : user.id === value
                          ? "opacity-100"
                          : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default UserSelect;
